import {useQuery} from '@tanstack/react-query'
import {UseFormReturn} from 'react-hook-form'
import {useMemo} from 'react'
import {QUERY_KEYS} from '@/queryClient'
import {useDebouncedValue} from '@/hooks/useDebouncedValue'
import {MailerFormValidationSchema, SendMailPayload} from '@/features/mailer/types'
import {buildRecipientsPayload} from '@/features/mailer/utils'
import {fetchPatientsCount, fetchConsultantsCount} from '@/features/mailer/http/mailer.http'

export interface MailerLiveCount {
    patients: number
    consultants: number
    total: number
    isLoading: boolean
    isError: boolean
}

export const useMailerLiveCount = (form: UseFormReturn<MailerFormValidationSchema>): MailerLiveCount => {
    // Stable string key avoids debounce timer reset when unrelated form fields (subject/body) change.
    const recipientsKey = JSON.stringify(buildRecipientsPayload(form.watch()))
    const debouncedKey = useDebouncedValue(recipientsKey, 400)
    const recipients = useMemo<SendMailPayload['recipients']>(() => JSON.parse(debouncedKey), [debouncedKey])

    const patientsQuery = useQuery({
        queryKey: [QUERY_KEYS.MAILER_LIVE_COUNT, 'patients', debouncedKey],
        queryFn: () => fetchPatientsCount(recipients),
        enabled: !!recipients.patients,
        staleTime: 30_000
    })

    const consultantsQuery = useQuery({
        queryKey: [QUERY_KEYS.MAILER_LIVE_COUNT, 'consultants', debouncedKey],
        queryFn: () => fetchConsultantsCount(recipients),
        enabled: !!recipients.consultants,
        staleTime: 30_000
    })

    const patients = recipients.patients ? (patientsQuery.data ?? 0) : 0
    const consultants = recipients.consultants ? (consultantsQuery.data ?? 0) : 0

    return {
        patients,
        consultants,
        total: patients + consultants,
        isLoading: patientsQuery.isFetching || consultantsQuery.isFetching,
        isError: patientsQuery.isError || consultantsQuery.isError
    }
}
